/**
 * Platform detection helper — safe to call on any runtime (web, iOS PWA,
 * Android Capacitor, iOS Capacitor). Does not throw if @capacitor/core
 * isn't loaded, so the web build stays lean.
 *
 * Google Play policy REQUIRES that apps in the Play Store use Google Play
 * Billing for digital subscriptions (§Payments). Our v1 Android build ships
 * without Play Billing SDK, so we must not surface any in-app "Subscribe /
 * Upgrade" button when running on Android — instead we point users to
 * https://nova.app to manage their subscription in a browser. Same pattern
 * as Netflix, Spotify, Kindle on Android.
 */

let cachedPlatform = null;

function detectPlatform() {
  // Prefer the Capacitor global if the native shell has injected it.
  try {
    // window.Capacitor is set by the native shell at load time.
    if (typeof window !== "undefined" && window.Capacitor) {
      const p = window.Capacitor.getPlatform?.();
      if (p) return p; // "ios" | "android" | "web"
    }
  } catch { /* ignore */ }

  // User-agent sniff as a last resort (rarely reached — we only rely on this
  // if the app is opened in a mobile browser or the Capacitor global fails).
  if (typeof navigator !== "undefined") {
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/android/.test(ua)) return "web-android";
    if (/iphone|ipad|ipod/.test(ua)) return "web-ios";
  }
  return "web";
}

export function getPlatform() {
  if (cachedPlatform) return cachedPlatform;
  cachedPlatform = detectPlatform();
  return cachedPlatform;
}

/** True only when running inside the native Android app (Capacitor). */
export function isAndroidNative() {
  return getPlatform() === "android";
}

/** True only when running inside the native iOS app (Capacitor). */
export function isIOSNative() {
  return getPlatform() === "ios";
}

/** True if any Capacitor-wrapped native build. */
export function isNative() {
  const p = getPlatform();
  return p === "ios" || p === "android";
}
